import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, DollarSign, Loader2 } from "lucide-react";
import {
  useTopUpWorkspaceWallet,
  useWorkspaceBilling,
  useCanManageBilling,
  isIndiaCard,
  isCheckoutRetryCode,
  isTopUpUnconfirmed,
} from "@/hooks/use-workspace-billing";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { formatMoney } from "@/lib/money";

// Mirrors the `billing` function's top-up and checkout routes.
const MIN_TOP_UP = 20;
const MAX_TOP_UP = 5000;
const AMOUNT_ERROR = `Enter an amount between ${formatMoney(MIN_TOP_UP)} and ${formatMoney(MAX_TOP_UP)}.`;
const PRESET_AMOUNTS = [20, 50, 100, 200];

interface TopUpDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Amount pre-filled when the dialog opens (from an "Add funds now" prompt). */
  presetAmount?: number;
}

export function TopUpDialog({ open, onOpenChange, presetAmount }: TopUpDialogProps) {
  const { data: workspaceBilling } = useWorkspaceBilling();
  const canManage = useCanManageBilling();
  const topUp = useTopUpWorkspaceWallet();
  const [internalOpen, setInternalOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;
  // Cards issued in India are charged customer-present through Checkout (RBI rules).
  const isIndia = isIndiaCard(workspaceBilling);
  // The last attempt may have charged the card: its retry must be the same id and amount (C6).
  const unconfirmed = isTopUpUnconfirmed(topUp.error);

  // One request id per top-up *attempt*, not per click or per open: an attempt whose
  // outcome is unknown survives closing and reopening the dialog, amount and all.
  useEffect(() => {
    if (!isOpen || unconfirmed) return;
    setRequestId(crypto.randomUUID());
    setSubmitted(false);
    topUp.reset();
    if (presetAmount) {
      setAmount(PRESET_AMOUNTS.includes(presetAmount) ? String(presetAmount) : "");
      setCustomAmount(PRESET_AMOUNTS.includes(presetAmount) ? "" : String(presetAmount));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const requestedAmount = parseFloat(customAmount || amount);
  const isValidAmount =
    Number.isFinite(requestedAmount) && requestedAmount >= MIN_TOP_UP && requestedAmount <= MAX_TOP_UP;
  const requiresAction = isCheckoutRetryCode((topUp.error as { code?: string } | null)?.code);
  const amountError = submitted && !isValidAmount ? AMOUNT_ERROR : null;

  const submit = async (viaCheckout = false) => {
    setSubmitted(true);
    if (!isValidAmount) return;

    try {
      await topUp.mutateAsync({ amount: requestedAmount, requestId, viaCheckout });
      setAmount("");
      setCustomAmount("");
      setIsOpen(false);
      setRequestId(crypto.randomUUID());
    } catch (error) {
      // A final answer ends this attempt, so the next one is new money. An unknown
      // outcome keeps the id: retrying it cannot charge the card twice.
      if (!isTopUpUnconfirmed(error)) setRequestId(crypto.randomUUID());
    }
  };

  if (!workspaceBilling || !canManage) {
    return null; // Loading, or a role the server refuses top-ups to.
  }

  // Check WORKSPACE payment method, not user profile
  if (!workspaceBilling.payment_method_added) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Please add a payment method first before topping up your wallet.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Top Up Wallet
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Top Up Wallet</DialogTitle>
          <DialogDescription>
            Add credits to your wallet. Minimum top-up is ${MIN_TOP_UP}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Select Amount</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {PRESET_AMOUNTS.map((preset) => (
                <Button
                  key={preset}
                  disabled={unconfirmed}
                  variant={amount === preset.toString() ? "default" : "outline"}
                  onClick={() => {
                    setAmount(preset.toString());
                    setCustomAmount("");
                  }}
                  className="w-full"
                >
                  ${preset}
                </Button>
              ))}
            </div>
          </div>

          <div className="relative">
            <Label htmlFor="top-up-amount">Or Enter Custom Amount</Label>
            <div className="flex items-center gap-2 mt-2">
              <DollarSign className="absolute left-3 text-muted-foreground" />
              <Input
                id="top-up-amount"
                type="number"
                disabled={unconfirmed}
                aria-invalid={!!amountError}
                aria-describedby={amountError ? "top-up-amount-error" : undefined}
                placeholder="20.00"
                min={MIN_TOP_UP}
                max={MAX_TOP_UP}
                step="0.01"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setAmount("");
                }}
                className="pl-8"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Minimum: {formatMoney(MIN_TOP_UP)} · Maximum: {formatMoney(MAX_TOP_UP)}
            </p>
            {amountError && (
              <p id="top-up-amount-error" className="text-sm text-destructive mt-1">
                {amountError}
              </p>
            )}
          </div>

          {unconfirmed && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                We couldn't confirm whether this top-up went through. Try again to finish it: the amount stays
                the same and your card will not be charged twice.
              </AlertDescription>
            </Alert>
          )}

          {requiresAction && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="space-y-3">
                <p>
                  {topUp.error.message} Finish it on
                  Stripe's secure checkout instead.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => submit(true)}
                >
                  Continue to secure checkout
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {isIndia && (
            <p className="text-xs text-muted-foreground">
              Cards issued in India are charged through Stripe's secure checkout so your bank can
              verify instantly.
            </p>
          )}

          <Button onClick={() => submit(isIndia)} disabled={topUp.isPending} className="w-full">
            {topUp.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {topUp.isPending
              ? "Processing..."
              : isIndia
                ? "Continue to secure checkout"
                : isValidAmount
                  ? `Add ${formatMoney(requestedAmount)} to Wallet`
                  : "Add to Wallet"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
